import { apiClient, getErrorMessage } from './apiClient'
import * as indexedDB from './indexedDBService'
import { addToSyncQueue } from './syncService'
import type { Industria } from '../types'
import type { PaginationResponse } from './clienteService'

export interface IndustriaFilters {
  page?: number
  limit?: number
  search?: string
  status?: 'all' | 'ativo' | 'inativo'
  sort?: string
}

export interface IndustriaCreateRequest {
  razaoSocial: string
  nomeFantasia: string
  cnpj: string
  email: string
  telefone: string
  cep: string
  logradouro: string
  numero: string
  complemento?: string
  bairro: string
  cidade: string
  estado: string
  cor?: string
}

// Função auxiliar para obter indústrias (exportada para sincronização)
export async function getIndustrias(): Promise<Industria[]> {
  const response = await apiClient.get<PaginationResponse<Industria>>('/industrias?limit=9999')
  return response.data.data
}

export const industriaService = {
  async getAll(filters?: IndustriaFilters): Promise<PaginationResponse<Industria>> {
    try {
      if (navigator.onLine) {
        const params = new URLSearchParams()
        if (filters?.page) params.append('page', filters.page.toString())
        if (filters?.limit) params.append('limit', filters.limit.toString())
        if (filters?.search) params.append('search', filters.search)
        if (filters?.status && filters.status !== 'all') params.append('status', filters.status)
        if (filters?.sort) params.append('sort', filters.sort)

        const response = await apiClient.get<PaginationResponse<Industria>>(`/industrias?${params}`)
        return response.data
      }

      const industrias = await indexedDB.getAll<Industria>(indexedDB.STORES.INDUSTRIAS)
      let filtered = industrias

      if (filters?.search) {
        const searchLower = filters.search.toLowerCase()
        filtered = industrias.filter(i => 
          i.razaoSocial?.toLowerCase().includes(searchLower) ||
          i.nomeFantasia?.toLowerCase().includes(searchLower) ||
          i.cnpj?.includes(searchLower)
        )
      }

      return {
        data: filtered,
        pagination: {
          page: 1,
          limit: filtered.length,
          total: filtered.length,
          totalPages: 1,
        }
      }
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async getById(id: string): Promise<Industria> {
    try {
      if (navigator.onLine) {
        const response = await apiClient.get<Industria>(`/industrias/${id}`)
        return response.data
      }

      const industria = await indexedDB.get<Industria>(indexedDB.STORES.INDUSTRIAS, id)
      if (!industria) throw new Error('Indústria não encontrada')
      return industria
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async create(data: IndustriaCreateRequest): Promise<Industria> {
    try {
      if (navigator.onLine) {
        const response = await apiClient.post<Industria>('/industrias', data)
        await indexedDB.put(indexedDB.STORES.INDUSTRIAS, response.data)
        return response.data
      }

      const tempId = `temp_${Date.now()}`
      const industria: Industria = {
        id: tempId,
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as Industria

      await indexedDB.put(indexedDB.STORES.INDUSTRIAS, industria)
      // Adiciona à fila com o ID temporário para referência
      await addToSyncQueue('CREATE', 'industria', { ...data, id: tempId })
      
      return industria
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async update(id: string, data: Partial<IndustriaCreateRequest>): Promise<Industria> {
    try {
      if (navigator.onLine) {
        const response = await apiClient.put<Industria>(`/industrias/${id}`, data)
        await indexedDB.put(indexedDB.STORES.INDUSTRIAS, response.data)
        return response.data
      }

      const industria = await indexedDB.get<Industria>(indexedDB.STORES.INDUSTRIAS, id)
      if (!industria) throw new Error('Indústria não encontrada')

      const updated = {
        ...industria,
        ...data,
        updatedAt: new Date().toISOString(),
      }

      await indexedDB.put(indexedDB.STORES.INDUSTRIAS, updated)
      await addToSyncQueue('UPDATE', 'industria', { id, ...data })
      
      return updated
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async delete(id: string): Promise<void> {
    try {
      if (navigator.onLine) {
        await apiClient.delete(`/industrias/${id}`)
        await indexedDB.remove(indexedDB.STORES.INDUSTRIAS, id)
        return
      }

      await indexedDB.remove(indexedDB.STORES.INDUSTRIAS, id)
      await addToSyncQueue('DELETE', 'industria', { id })
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },
}
